/**
 * NewProjectDialog — modal to create a workspace project.
 *
 * Single-form (no wizard): name + icon + color. Defaults to brand
 * (Folder / orange) so the user can ship with one click.
 *
 * On success: invalidates ``projectsQueryKey`` (server list) AND selects
 * the new project as ``currentProjectId`` so the pill immediately reflects
 * the change.
 */

import { Trans, useLingui } from "@lingui/react/macro";
import * as React from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@ando/ui";

import { useCreateProject } from "../hooks/useProjects.js";
import { selectSetCurrentProjectId, useProjectsStore } from "../state/projectsStore.js";
import {
  DEFAULT_PROJECT_COLOR_TOKEN,
  DEFAULT_PROJECT_ICON_NAME,
  PROJECT_ICONS,
  ProjectIcon,
} from "./ProjectIcon.js";

export interface NewProjectDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

interface FormState {
  name: string;
  icon: string;
  color: string;
}

const EMPTY_STATE: FormState = {
  name: "",
  icon: DEFAULT_PROJECT_ICON_NAME,
  color: DEFAULT_PROJECT_COLOR_TOKEN,
};

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({ open, onOpenChange }) => {
  const { t, i18n } = useLingui();
  const [state, setState] = React.useState<FormState>(EMPTY_STATE);
  const setCurrentProjectId = useProjectsStore(selectSetCurrentProjectId);
  const mutation = useCreateProject();
  // Focus the name input on open without triggering the noAutofocus lint —
  // Radix Dialog handles focus return on close, so a direct ref + effect
  // is the recommended pattern (see ./packages/ui/src/primitives/dialog.tsx).
  const nameRef = React.useRef<HTMLInputElement>(null);

  // Reset on close + focus name input on open.
  React.useEffect(() => {
    if (!open) {
      setState(EMPTY_STATE);
      return;
    }
    // Defer focus until the Radix Dialog content has mounted + Radix's
    // own focus-trap initial focus settles. ``queueMicrotask`` is enough
    // because Radix focuses the first focusable child synchronously.
    queueMicrotask(() => nameRef.current?.focus());
  }, [open]);

  const trimmedName = state.name.trim();
  const canSubmit = trimmedName.length > 0 && !mutation.isPending;

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) return;
      mutation.mutate(
        {
          name: trimmedName,
          icon: state.icon,
          color: state.color,
        },
        {
          onSuccess: (created) => {
            setCurrentProjectId(created.project_id);
            onOpenChange(false);
          },
        },
      );
    },
    [canSubmit, mutation, trimmedName, state.icon, state.color, setCurrentProjectId, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              <Trans>Nuovo progetto</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Raggruppa le conversazioni per area di lavoro. Potrai cambiare icona, colore e
                impostazioni in qualsiasi momento.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="project-name" className="font-medium text-[12px] text-ink-2">
              <Trans>Nome progetto</Trans>
            </label>
            <input
              ref={nameRef}
              id="project-name"
              type="text"
              required
              value={state.name}
              onChange={(event) => setState((prev) => ({ ...prev, name: event.target.value }))}
              placeholder={t`es. Vendite Q2`}
              className={cn(
                "h-9 rounded-md border border-hairline bg-white/85 px-3",
                "text-[13px] text-ink-1 placeholder:text-ink-3",
                "focus-visible:border-ando-orange/45 focus-visible:outline-none",
                "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-[12px] text-ink-2">
              <Trans>Icona</Trans>
            </span>
            <div className="grid grid-cols-8 gap-1.5">
              {PROJECT_ICONS.map((icon) => {
                const selected = icon.name === state.icon;
                const Icon = icon.Component;
                // Swatch matches the refreshed Linear-style chrome:
                // monochrome glyph on a quiet cream surface. The active
                // state is signalled by the orange ring + ink-1 glyph,
                // not by a saturated fill (which would clash with the
                // companion mark like the old fg+bg pattern did).
                return (
                  <button
                    key={icon.name}
                    type="button"
                    aria-label={i18n._(icon.label)}
                    aria-pressed={selected}
                    onClick={() => setState((prev) => ({ ...prev, icon: icon.name }))}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-md transition-colors",
                      "border border-hairline",
                      selected
                        ? "bg-cream text-ink-1 ring-2 ring-ando-orange/45"
                        : "bg-cream/60 text-ink-2 hover:bg-cream-deep hover:text-ink-1",
                    )}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-hairline bg-cream/30 px-3 py-2">
            <ProjectIcon iconName={state.icon} colorToken={state.color} />
            <span className="font-medium text-[13px] text-ink-1">
              {trimmedName.length > 0 ? trimmedName : t`Anteprima progetto`}
            </span>
          </div>

          {mutation.isError ? (
            <p className="text-[12px] text-rose-600">
              {mutation.error instanceof Error
                ? mutation.error.message
                : t`Errore nella creazione del progetto.`}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? t`Creazione...` : t`Crea`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

NewProjectDialog.displayName = "NewProjectDialog";
